import { z } from 'zod';
/**
 * Cash-request domain constants shared by the web form and the core API's
 * validation. A request is either *additional* cash (the store needs more
 * money in the drawer/safe) or a cash *exchange* (swap large bills for the
 * listed smaller denominations — the store hands back equal value).
 *
 * The denomination breakdown records what the store should receive, as
 * counts per unit. Coin denominations are requested as standard US bank
 * rolls, not loose coins.
 */
export const CASH_REQUEST_TYPES = ['additional', 'exchange'];
export const CashRequestTypeSchema = z.enum(CASH_REQUEST_TYPES);
export const CASH_DENOMINATION_KEYS = [
    'bill_100',
    'bill_50',
    'bill_20',
    'bill_10',
    'bill_5',
    'bill_1',
    'roll_quarters',
    'roll_dimes',
    'roll_nickels',
    'roll_pennies',
];
export const CASH_DENOMINATIONS = [
    { key: 'bill_100', label: '$100', valueCents: 100_00, kind: 'bill' },
    { key: 'bill_50', label: '$50', valueCents: 50_00, kind: 'bill' },
    { key: 'bill_20', label: '$20', valueCents: 20_00, kind: 'bill' },
    { key: 'bill_10', label: '$10', valueCents: 10_00, kind: 'bill' },
    { key: 'bill_5', label: '$5', valueCents: 5_00, kind: 'bill' },
    { key: 'bill_1', label: '$1', valueCents: 1_00, kind: 'bill' },
    { key: 'roll_quarters', label: 'Quarters', valueCents: 10_00, kind: 'roll', detail: '40 × 25¢' },
    { key: 'roll_dimes', label: 'Dimes', valueCents: 5_00, kind: 'roll', detail: '50 × 10¢' },
    { key: 'roll_nickels', label: 'Nickels', valueCents: 2_00, kind: 'roll', detail: '40 × 5¢' },
    { key: 'roll_pennies', label: 'Pennies', valueCents: 50, kind: 'roll', detail: '50 × 1¢' },
];
const DENOMINATION_BY_KEY = new Map(CASH_DENOMINATIONS.map((d) => [d.key, d]));
export function cashDenomination(key) {
    const d = DENOMINATION_BY_KEY.get(key);
    if (!d)
        throw new Error(`unknown cash denomination: ${key}`);
    return d;
}
/**
 * Sparse breakdown: unit key → count requested. Only nonzero entries are
 * stored. 999 per unit is far above any plausible store request and keeps
 * the serialized column bounded.
 */
export const CashBreakdownSchema = z
    .record(z.enum(CASH_DENOMINATION_KEYS), z.number().int().min(1).max(999))
    .refine((b) => Object.keys(b).length > 0, { message: 'at least one denomination required' });
export function breakdownTotalCents(breakdown) {
    return Object.entries(breakdown).reduce((sum, [key, qty]) => sum + cashDenomination(key).valueCents * (qty ?? 0), 0);
}
//# sourceMappingURL=cash.js.map